/**
 * The one-shot entrance for whichever screen answers the `home` route.
 *
 * ONCE PER PAGE LOAD, NOT PER MOUNT. Backing out of `play` to change your mind
 * lands on home again, and a screen that re-choreographs itself every time you
 * return stops reading as polish and starts reading as a wait — which is the
 * one thing `HomeScreen` has spent its whole history refusing to put between a
 * player and a match.
 *
 * SHARED BETWEEN THE TWO LANDING SCREENS. `FirstRunScreen` and `HomeScreen` are
 * alternatives for the same route, so the claim lives here rather than in
 * either of them: whichever renders first spends it, and dismissing the intro
 * to arrive at home does not replay the whole thing a second time.
 */

import { useEffect, useRef, useState } from 'react';

/**
 * Long enough for the last child to land: 225ms of stagger plus a 340ms rise,
 * as laid out under `Entrance` in `index.css`. Kept a touch over the sum so the
 * attribute never disappears mid-animation.
 */
const ENTRANCE_MS = 600;

let entranceSpent = false;

/**
 * True for the first mount of a page load, then false forever after.
 *
 * Callers put it on the screen root as `data-entering`; the stagger is entirely
 * CSS from there. It goes false again ~0.6s in, and THAT is what keeps this an
 * entrance rather than a transition — panels opened by a later click must not
 * inherit a delay that was meant for a page load, because on a control you just
 * pressed a delay reads as lag.
 */
export function useEntrance(): boolean {
  /*
   * A ref and not a `useState` initialiser. StrictMode invokes initialisers
   * twice and keeps the SECOND result, so an initialiser would spend the
   * entrance on its own first call and hand the real mount a `false`. The
   * screen would animate in production and never in development, which is the
   * worst way round to find out.
   */
  const claimed = useRef<boolean | undefined>(undefined);
  if (claimed.current === undefined) {
    claimed.current = !entranceSpent;
    entranceSpent = true;
  }

  const [entering, setEntering] = useState(claimed.current);

  useEffect(() => {
    if (!entering) return undefined;
    const timer = window.setTimeout(() => setEntering(false), ENTRANCE_MS);
    return () => window.clearTimeout(timer);
  }, [entering]);

  return entering;
}

/** Test seam. Nothing in the app calls this; a page load is the real reset. */
export function resetEntranceForTests(): void {
  entranceSpent = false;
}
